import React, { FC, useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { APP_URLS } from 'src/constants';
import { Modal, BareModal, PrimaryButton, SecondaryButton  } from '@cb/apricot-react';

import { Checkbox, Input } from 'src/generic/component/form';
import { ISummary } from 'src/types';
import { IAIRequest, IAIData, IAI, IOrg, IAIAppForm } from 'src/types/org/ai/schoolInterface';
import { ITestingPlan } from 'src/types/org/common';

import { Panel, FieldRow, FieldLabel, ErrorNotice, ContainerInline, HeaderInline, Loader } from 'src/generic/component';
import { ErrorManager, ValidationError, IFieldErrors, ERRORS_EMPTY } from 'src/types/validation';
import { IRequestMeta } from 'src/types/org/request';
import { AIFields } from 'src/containers/school/ai/aiForm';
import { TestingPlanFields } from '../common/testingPlanForm';
import { Draft } from 'src/types/common';
import { saveAIForm, submitAIForm } from 'src/apis';
import { validate as aiValidate, validateAI } from 'src/types/org/ai/aiCreateSchema';
import { validate as testingPlanValidate } from 'src/types/org/common/testingPlanSchema';
import { isOnsiteHoursLessThan15 } from 'src/utils/schoolCreateFormUtils';
import {hasNonEmptyFields} from 'src/utils/utils';

interface IAIRequestProps {
  item: ISummary;
  request: Draft<IAIRequest>;
  readOnly?: boolean;
}

export const AIRequestForm: FC<IAIRequestProps> = ({ item, request: initial, readOnly }) => {
  const navigate = useNavigate();

  const [request, setRequest] = useState<Draft<IAIRequest>>(initial || {});
  const [step, setStep] = useState('ai');

  const [appliedLevel, setAppliedLevel] = useState<'1' | '2' | undefined>(initial?.appliedLevel);

  const [ai, setAi] = useState<Draft<IAI>>(initial?.data?.ai || {});

  const [org, setOrg] = useState<Draft<IOrg>>(initial?.data?.org || {
    name: '',
    email: '',
    phone: '',
    website: '',
    visitingAddress: {},
    shippingAddress: undefined,
    shippingPolicyException: undefined
  });

  const [appForm, setAppForm] = useState<Draft<IAIAppForm>>(initial?.data?.appForm || {
    isSchoolDistrictMember: false,
    schoolDistricts: '',
    establishedDate: '',
    sharedOrgAddress: false,
    sharedOrgAddressInfo: '',
    existingOrg: false,
    existingOrgInfo: '',
    hasCollegeBoardCode: false,
    collegeBoardCodes: '',
    mergerSchoolCodes: '',
    // supportFiles: [],
    type: [],
    typeOther: '',
    creditType: [],
    creditTypeOther: '',
    academicDisciplines: [],
    academicDisciplinesOther: '',
    teachingMethod: [],
    teachingMethodOther: '',
    onsiteStudents: {
      zeroTaught: '',
      oneTaught: '',
      twoTaught: '',
      threeTaught: '',
      fourTaught: '',
      fiveTaught: '',
      sixTaught: '',
      sevenTaught: '',
      eightTaught: '',
      nineTaught: '',
      tenTaught: '',
      elevenTaught: '',
      twelveTaught: ''
    },
    studentsEnrolled: {
      zeroEnrolled: '', oneEnrolled: '', twoEnrolled: '', threeEnrolled: '',
      fourEnrolled: '', fiveEnrolled: '', sixEnrolled: '', sevenEnrolled: '',
      eightEnrolled: '', nineEnrolled: '', tenEnrolled: '', elevenEnrolled: '', twelveEnrolled: ''
    },
    examsInformation: {
      interestedInAPCourses: undefined,
      apCoursesOffered: undefined,
      preAPCoursesOffered: undefined,
      confirmAPGradeLevels: undefined,
      interestedInPreAPCourses: undefined,
      interestedInPSATSuite: undefined,
      interestedInSAT: undefined,
      satAdministrationTypes: undefined,
      earliestAdministrationDate: undefined
    },
    accreditation: initial?.data?.appForm?.accreditation || {
      hasAccreditation: undefined,
      accreditationDetails: undefined,
      materials: []
    },
    // Preserve existing nested objects from initial
    principalName: initial?.data?.appForm?.principalName,
    principalEmail: initial?.data?.appForm?.principalEmail,
    principalWebpage: initial?.data?.appForm?.principalWebpage,
    studentSchedule: initial?.data?.appForm?.studentSchedule,
    aiTestPrep: initial?.data?.appForm?.aiTestPrep,
    aiStaff: initial?.data?.appForm?.aiStaff,
    examSecurity: initial?.data?.appForm?.examSecurity,
    submitterComments: initial?.data?.appForm?.submitterComments
  });

  // console.log('AI Request Form Initial Data:', initial?.data);
  // console.log('Accreditation Files data:', initial?.data?.appForm?.accreditation?.materials);

  const [testingPlan, setTestingPlan] = useState<Draft<ITestingPlan>>(initial?.data?.testingPlan || {});

  const metaInitial = {
    creator: initial?.meta?.creator || item.creator,
    creatorEmail: initial?.meta?.creatorEmail || item.creatorEmail,
    submitter: readOnly ? initial?.meta?.submitter : undefined,
    disclaimer: readOnly ? initial?.meta?.disclaimer : undefined
  };

  const [meta, setMeta] = useState<Draft<IRequestMeta>>(metaInitial);

  const draft: Draft<IAIRequest> = {
    userId: item.userId,
    categoryId: item.categoryId,
    appliedLevel: appliedLevel,
    data: {
      version: item.version, 
      ai: ai,                    // ← AI data 
      org: org,                  // ← Organization data
      appForm: appForm,          // ← Application form data
      testingPlan: testingPlan   // ← Testing plan data
    },
    meta
  };

  const getCurrentLevel = (): '1' | '2' => {
    return (draft.appliedLevel || draft.data?.ai?.appliedLevel || appliedLevel || '1') as '1' | '2';
  };

  // If user changes applied level to 1, reset all AI fields that are only relevant for level 2
  const buildNormalizedDraft = (): Draft<IAIRequest> => {
    const level = getCurrentLevel();
    return {
      ...draft,
      appliedLevel: level,
      data: {
        ...draft.data,
        ai: {
          ...draft.data?.ai,
          appliedLevel: level
        }
      }
    };
  };

  // Use useMemo to reactively determine if testing plan is required
  // Testing plan is required when: appliedLevel is '2' AND on-site hours < 15
  const requireTestingPlan = useMemo(() => {

    // If item.requireTestingPlan or item.requireTestingPlanUpdates is true, when status is NMI, then it requires testing plan
    const isTrue = (val: unknown) => val === true || val === 'true';

    if (isTrue(item.requireTestingPlan) || isTrue(item.requireTestingPlanUpdates)) {
      return true;
    }

    // Check if testingPlan exists and has populated fields for old AI form's View to display TP
    if (hasNonEmptyFields(initial?.data?.testingPlan)) {
      return true;
    }

    // Must be level 2
    const currentLevel = getCurrentLevel();
    if (currentLevel !== '2') {
      return false;
    }

    // AND on-site hours must be < 15
    const instructionDays = appForm?.studentSchedule?.instructionDays;
    const hoursOnsite = appForm?.studentSchedule?.hoursOnsite;

    return isOnsiteHoursLessThan15(instructionDays, hoursOnsite);
  }, [
  appliedLevel,
  ai?.appliedLevel,
  appForm?.studentSchedule?.instructionDays,
  appForm?.studentSchedule?.hoursOnsite,
  item.requireTestingPlan,
  item.requireTestingPlanUpdates,
  initial?.data?.testingPlan
]);

  // Check if AI section is completed
  let aiCompleted = false;
  try {
    const currentLevel = getCurrentLevel();
    if (currentLevel) {
      validateAI(currentLevel, org, appForm);
      aiCompleted = true;
    }
  } catch (error) { }

  let testingPlanCompleted = false;
  try {
    testingPlanValidate(testingPlan);
    testingPlanCompleted = true;
  } catch (error) { }

  useEffect(() => {
    setRequest(initial || {});
    setAppliedLevel(initial?.appliedLevel);

    setAi(initial?.data?.ai || {});

    setOrg(initial?.data?.org || {
      name: '',
      email: '',
      phone: '',
      website: '',
      visitingAddress: {},
      shippingAddress: undefined,
      shippingPolicyException: undefined
    });

    setAppForm(initial?.data?.appForm || {
      isSchoolDistrictMember: false,
      schoolDistricts: '',
      establishedDate: '',
      sharedOrgAddress: false,
      sharedOrgAddressInfo: '',
      existingOrg: false,
      existingOrgInfo: '',
      hasCollegeBoardCode: false,
      collegeBoardCodes: '',
      mergerSchoolCodes: '',
      type: [],
      typeOther: '',
      creditType: [],
      creditTypeOther: '',
      academicDisciplines: [],
      academicDisciplinesOther: '',
      teachingMethod: [],
      teachingMethodOther: '',
      // Preserve accreditation data
      accreditation: initial?.data?.appForm?.accreditation || {
        hasAccreditation: undefined,
        accreditationDetails: undefined,
        materials: []
      },
      // Preserve other nested objects
      onsiteStudents: initial?.data?.appForm?.onsiteStudents || {
        zeroTaught: '',
        oneTaught: '',
        twoTaught: '',
        threeTaught: '',
        fourTaught: '',
        fiveTaught: '',
        sixTaught: '',
        sevenTaught: '',
        eightTaught: '',
        nineTaught: '',
        tenTaught: '',
        elevenTaught: '',
        twelveTaught: ''
      },
      studentsEnrolled: initial?.data?.appForm?.studentsEnrolled || {
        zeroEnrolled: '', oneEnrolled: '', twoEnrolled: '', threeEnrolled: '',
        fourEnrolled: '', fiveEnrolled: '', sixEnrolled: '', sevenEnrolled: '',
        eightEnrolled: '', nineEnrolled: '', tenEnrolled: '', elevenEnrolled: '', twelveEnrolled: ''
      },
      examsInformation: initial?.data?.appForm?.examsInformation || {
        interestedInAPCourses: undefined,
        apCoursesOffered: undefined,
        preAPCoursesOffered: undefined,
        confirmAPGradeLevels: undefined,
        interestedInPreAPCourses: undefined,
        interestedInPSATSuite: undefined,
        interestedInSAT: undefined,
        satAdministrationTypes: undefined,
        earliestAdministrationDate: undefined
      },
      //  Preserve other existing fields
      studentSchedule: initial?.data?.appForm?.studentSchedule,
      aiTestPrep: initial?.data?.appForm?.aiTestPrep,
      aiStaff: initial?.data?.appForm?.aiStaff,
      examSecurity: initial?.data?.appForm?.examSecurity,
      submitterComments: initial?.data?.appForm?.submitterComments,
      principalName: initial?.data?.appForm?.principalName,
      principalEmail: initial?.data?.appForm?.principalEmail,
      principalWebpage: initial?.data?.appForm?.principalWebpage
    });

    setTestingPlan(initial?.data?.testingPlan || {});

    setMeta(metaInitial);
  }, [initial]);

  const [validating, setValidating] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [errorMessage, setErrorMessage] = useState<string | React.ReactNode>('');
  const [errorMessageOpen, setErrorMessageOpen] = useState<boolean>(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState<boolean>(false);
  const [confirmCheckbox, setConfirmCheckbox] = useState<boolean>(false);
  const [aiErrors, setAIErrors] = useState<IFieldErrors>(ERRORS_EMPTY);
  const [testingPlanErrors, setTestingPlanErrors] = useState<IFieldErrors>(ERRORS_EMPTY);
  const [metaErrors, setMetaErrors] = useState<IFieldErrors>(ERRORS_EMPTY);
  const [levelError, setLevelError] = useState<string>('');
  const { getError: getMetaError } = ErrorManager(metaErrors);

  const validateLevel = (appliedLevel: '1' | '2' | undefined) => {
    setLevelError('');
    if (!appliedLevel) {
      const error = `Applied level must be either '1' or '2'`;
      setLevelError(error);
      throw new ValidationError({
        _errors: [error]
      });
    }
    return appliedLevel;
  };

  const validateTestingPlanSection = (data: Draft<ITestingPlan>) => {
    try {
      const validated = testingPlanValidate(data);
      setTestingPlanErrors(ERRORS_EMPTY);
      return validated;
    } catch (error: any) {
      if (error instanceof ValidationError) {
        setTestingPlanErrors(error.fieldErrors);
      } else {
        setErrorMessage(error.message || 'Testing plan validation failed');
      }
      return null;
    }
  };

  const validateAiSection = (appliedLevel: '1' | '2' | undefined, orgData: Draft<IOrg>, appFormData: Draft<IAIAppForm>) => {
    try {
      const level = validateLevel(appliedLevel);
      const validated = validateAI(level, orgData, appFormData);
      setAIErrors(ERRORS_EMPTY);
      return validated;
    } catch (error: any) {

      console.log('=== VALIDATION ERROR ===');
      console.log('Error:', error);
      console.log('Error message:', error.message);
      console.log('Error fieldErrors:', error.fieldErrors);
      console.log('========================');

      if (error instanceof ValidationError) {
        const panelErrors: any = { data: {} };

        if (error.fieldErrors) {
          Object.keys(error.fieldErrors).forEach(key => {
            if (key === 'org' || key === 'appForm' || key === 'ai') {
              panelErrors.data[key] = (error.fieldErrors as any)[key];
            } else {
              panelErrors[key] = (error.fieldErrors as any)[key];
            }
          });
        }
        console.log('Final panelErrors:', panelErrors);
        setAIErrors(panelErrors);
      } else {
        setErrorMessage(error.message || 'AI validation failed');
      }
      return null;
    }
  };

  // validateFullRequest uses requireTestingPlan variable
  const validateFullRequest = (appliedLevel: '1' | '2', data: Draft<IAIData>, metaData: Draft<IRequestMeta>) => {
    try {
      const level = validateLevel(appliedLevel);

      const aiValidated = aiValidate(level, data);

      // Use requireTestingPlan to conditionally validate testing plan
      if (requireTestingPlan && data.testingPlan) {
        testingPlanValidate(data.testingPlan);
      }

      if (!metaData.disclaimer) {
        throw new ValidationError({
          meta: {
            disclaimer: { _errors: ['You must agree to the disclaimer'] }
          }
        });
      }

      if (!metaData.submitter || metaData.submitter.trim().length === 0) {
        throw new ValidationError({
          meta: {
            submitter: { _errors: ['Submitter name is required'] }
          }
        });
      }

      setAIErrors(ERRORS_EMPTY);
      setTestingPlanErrors(ERRORS_EMPTY);
      setMetaErrors(ERRORS_EMPTY);

      return {
        ...aiValidated,
        testingPlan: data.testingPlan
      };
    } catch (error: any) {
      if (error instanceof ValidationError) {
        const panelErrors: any = { data: {} };

        if (error.fieldErrors) {
          Object.keys(error.fieldErrors).forEach(key => {
            const errorValue = (error.fieldErrors as any)[key];

            if (key === 'org' || key === 'appForm' || key === 'ai') {
              panelErrors.data[key] = errorValue;
            } else if (key === 'meta') {
              setMetaErrors(errorValue || ERRORS_EMPTY);
            } else {
              panelErrors[key] = errorValue;
            }
          });
        }

        setAIErrors(panelErrors);
      } else {
        setErrorMessage(error.message || 'Validation failed');
      }
      return null;
    }
  };


  const handleSave = async () => {
    setSubmitting(true);
    try {
      const normalizedDraft = buildNormalizedDraft();
      const response = await saveAIForm(normalizedDraft);
      setErrorMessage('');
      setErrorMessageOpen(false);
      setSubmitting(false);
      setRequest(normalizedDraft);
      return response;
    } catch (error: any) {
      let message = "An unknown server error occurred, the save was unsuccessful.";
      if (error instanceof ValidationError) {
        const { getFieldError } = ErrorManager(error.fieldErrors);
        setAIErrors(getFieldError('data.ai') || getFieldError('data.org') || getFieldError('data.appForm') || ERRORS_EMPTY);
        setTestingPlanErrors(getFieldError('data.testingPlan') || ERRORS_EMPTY);
        setValidating(true);
        message = "The save failed as some of the entered fields are invalid.";
      } else {
        console.log(error?.message || error);
      }
      setErrorMessageOpen(true);
      setErrorMessage(message);
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    setValidating(true);
    try {
      const normalizedDraft = buildNormalizedDraft();
      const currentLevel = getCurrentLevel();
      const validated = validateFullRequest(currentLevel, normalizedDraft.data!, meta);
      if (validated) {
        // Show confirmation modal instead of submitting directly
        setConfirmModalOpen(true);
        setConfirmCheckbox(false);
      }
    } catch (error: any) {
      if (error instanceof ValidationError) {
        const { getFieldError } = ErrorManager(error.fieldErrors);
        setAIErrors(getFieldError('data.ai') || getFieldError('data.org') || getFieldError('data.appForm') || ERRORS_EMPTY);
        setTestingPlanErrors(getFieldError('data.testingPlan') || ERRORS_EMPTY);
        setMetaErrors(getFieldError('meta') || ERRORS_EMPTY);
      } else {
        setErrorMessage(error.message || 'Submission failed');
      }
    }finally {
    setValidating(false);
  }
  };

  const handleConfirmSubmit = async () => {
    if (!confirmCheckbox) {
      return; // Don't submit if checkbox not checked
    }
    setConfirmModalOpen(false);
    setSubmitting(true);
    try {
      const normalizedDraft = buildNormalizedDraft()
      await submitAIForm(normalizedDraft);
      navigate(APP_URLS.HOME);
    } catch (error: any) {
      if (error instanceof ValidationError) {
        const { getFieldError } = ErrorManager(error.fieldErrors);
        setAIErrors(getFieldError('data.ai') || getFieldError('data.org') || getFieldError('data.appForm') || ERRORS_EMPTY);
        setTestingPlanErrors(getFieldError('data.testingPlan') || ERRORS_EMPTY);
        setMetaErrors(getFieldError('meta') || ERRORS_EMPTY);
      } else {
        setErrorMessage(error.message || 'Submission failed');
      }
    }
    setSubmitting(false);
  };

  const handleClose = () => {
    navigate(APP_URLS.HOME);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    setErrorMessageOpen(false);
    setErrorMessage(false);
  }, [step]);

  const scrollToError = () => {
    const element = document.querySelector<HTMLElement>('.cb-validation-error');
    if (element) {
      const top = element.getBoundingClientRect().top;
      window.scrollBy({ top: top - 100 });
    }
  };

  // handleNext uses requireTestingPlan variable
  const handleNext = async () => {
    let isValid = false;
    let nextStep = 'submit';
    if (step === 'ai') {
      console.log('aiRequesForm : Require Testing Plan:', requireTestingPlan);
      nextStep = requireTestingPlan ? 'testingPlan' : 'submit';
    }

    if (!readOnly) {
      const normalizedDraft = buildNormalizedDraft();
      const currentLevel = getCurrentLevel();
      const validated = (step === 'ai') ?
        validateAiSection(appliedLevel, org, appForm) :
        validateTestingPlanSection(normalizedDraft.data?.testingPlan || {});

      if (validated) {
        if (await handleSave()) {
          isValid = true;
        }
      } else {
        setValidating(true);
        setErrorMessageOpen(true);
        setErrorMessage(<div>There were invalid fields still in the form. Look for errors near your fields.</div>);
      }
    } else {
      isValid = true;
    }

    if (isValid) setStep(nextStep);
  };

  const SaveButton: FC = () => {
    return <PrimaryButton style={{ marginBottom: '10px', width: '100%' }} onClick={async () => {
      await handleSave();
    }} disabled={submitting}>Save</PrimaryButton>;
  };

  let breadcrumb = 'ai-breadcrumb';
  let submitBreadcrumb = breadcrumb;
  let testingPlanBreadcrumb = breadcrumb;
  let aiBreadcrumb = breadcrumb;

  if (step === 'ai') {
    aiBreadcrumb += ' active';
  }

  if (step === 'testingPlan') {
    testingPlanBreadcrumb += ' active';
  } else if (!aiCompleted) {
    testingPlanBreadcrumb += ' disabled';
  }

  const submitDisabled = (requireTestingPlan && !testingPlanCompleted) || !aiCompleted;

  if (submitDisabled) {
    submitBreadcrumb += ' disabled';
  } else if (step === 'submit') {
    submitBreadcrumb += ' active';
  }

  let form = <Loader />;
  if (request) {
    if (step === 'ai') {
      form = <>
        <AIFields
          categoryId={item.categoryId}
          userId={item.userId}
          item={draft}
          readOnly={readOnly}
          errors={aiErrors}
          onUpdate={async (data: Draft<IAIRequest>) => {
            if (validating) {
              const levelForValidation = (data.appliedLevel || data.data?.ai?.appliedLevel || '1') as '1' | '2'; 
              validateAiSection(levelForValidation, data.data?.org || {}, data.data?.appForm || {});
            }
            setAi(data.data?.ai || {});
            setOrg(data.data?.org || {});
            setAppForm(data.data?.appForm || {});
            setAppliedLevel(data.appliedLevel);
          }}
        />
        <br />
        {!readOnly && <SaveButton />}
        <PrimaryButton style={{ marginRight: '10px', width: '100%' }} onClick={handleNext} disabled={submitting}>Next</PrimaryButton>
        {readOnly && (
          <SecondaryButton type="button" style={{ width: '100%', marginTop: '10px' }} disabled={submitting} onClick={handleClose}>
            Close
          </SecondaryButton>
        )}
      </>;
    }
    //Use requireTestingPlan to conditionally render testing plan step
    if ((step === 'testingPlan' && requireTestingPlan)) {
      form = <><TestingPlanFields categoryId={item.categoryId} userId={item.userId} item={testingPlan} readOnly={readOnly} errors={testingPlanErrors}
        onUpdate={async (data) => {
          if (validating) {
            validateTestingPlanSection(data);
          }
          setTestingPlan(data);
        }} />
        <br />
        {!readOnly && <SaveButton />}
        <PrimaryButton style={{ marginRight: '10px', width: '100%' }} onClick={handleNext} disabled={submitting}>Next</PrimaryButton>
        {readOnly && (
          <SecondaryButton type="button" style={{ width: '100%', marginTop: '10px' }} disabled={submitting} onClick={handleClose}>
            Close
          </SecondaryButton>
        )}
      </>;
    } else if (step === 'submit') {
      form = <>
        <Panel>
          <h4 style={{ marginBottom: "20px" }}>Disclaimer</h4>
          <FieldRow>
            <FieldLabel variant="body1" style={{ marginBottom: '5px' }}>By submitting this request I confirm that all of the information provided is true and accurately describes the organization named on this form and have the authority to make this request. I understand that if any of the information is false, deactivation of the school code and/or legal action may result.</FieldLabel>
            <Checkbox disabled={readOnly} label='Accept' style={{ display: 'inline-block' }} checked={meta.disclaimer || false} onChange={() => {
              setMeta({ ...meta, disclaimer: meta.disclaimer ? !meta.disclaimer : true });
            }} />
            {getMetaError('disclaimer') &&
              <span className='cb-input-helper cb-validation-error'>You must agree to the disclaimer</span>
            }
          </FieldRow>

          <FieldRow>
            <FieldLabel variant="body1">Submitter</FieldLabel>
            <Input
              disabled={readOnly}
              validation={getMetaError('submitter') ? 'error' : undefined}
              validationMsg={getMetaError('submitter')}
              onChange={(e: any) => { setMeta({ ...meta, submitter: e.target.value }); }} value={meta.submitter} />
          </FieldRow>
        </Panel>
        {!readOnly &&
          <PrimaryButton style={{ marginRight: '10px', width: '100%' }} onClick={async () => {
            await handleSubmit();
          }} disabled={submitting}>Submit</PrimaryButton>
        }
        {readOnly && (
          <SecondaryButton type="button" style={{ width: '100%', marginTop: '10px' }} disabled={submitting} onClick={() => navigate(APP_URLS.HOME)}>
            Close
          </SecondaryButton>
        )}
      </>;
    }
  }

  return (<div>
    {/* Error Modal */}
    <Modal withHeader={false} withFooter={true}
      open={errorMessageOpen} onClose={() => { setErrorMessageOpen(false); scrollToError(); }}>
      <ContainerInline>
        <HeaderInline style={{ marginBottom: '6px' }}>
          <i style={{ marginRight: '10px' }} className="cb-icon cb-icon-circular cb-exclamation cb-red1-color" aria-hidden="true"></i>
          <h4 className="cb-notification-title"><strong>Errors Occurred</strong></h4>
        </HeaderInline>
        <div style={{ marginLeft: '42px' }} className="cb-notification-content">
          {errorMessage}
        </div>
      </ContainerInline>
    </Modal>

    {/* Loading Modal */}
    <BareModal open={submitting}>
      <Loader />
    </BareModal>

    {/* Confirmation Modal */}
    <Modal withHeader={false} withFooter={false}
      open={confirmModalOpen} onClose={() => { setConfirmModalOpen(false); setConfirmCheckbox(false); }}>
      <ContainerInline>
        <div className="cb-notification-content" style={{ marginBottom: '20px', color: '#324DC7', fontWeight: 600 }}>
          <p style={{ marginBottom: '15px' }}>
            Requests for a new College Board School Code or to update an existing College Board School Code to Level II must be submitted by the <strong>head of school or district</strong>.
          </p>
          <p style={{ marginBottom: '15px' }}>
            Please click the checkbox to confirm:
          </p>
          <Checkbox
            label={<span>I understand applications submitted by unauthorized users <strong>can not be processed</strong>.</span>}
            checked={confirmCheckbox}
            onChange={() => setConfirmCheckbox(!confirmCheckbox)}
          />
        </div>
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <PrimaryButton onClick={handleConfirmSubmit} disabled={!confirmCheckbox}>Submit</PrimaryButton>
          <PrimaryButton onClick={() => { setConfirmModalOpen(false); setConfirmCheckbox(false); }}>Cancel</PrimaryButton>
        </div>
      </ContainerInline>
    </Modal>

    {/* Breadcrumb Navigation with Save button */}
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div className={aiBreadcrumb} style={{ marginRight: '10px' }} onClick={() => { setStep('ai'); }}>School Info</div> &gt;
        {requireTestingPlan &&
          <><div className={testingPlanBreadcrumb} style={{ marginRight: '10px', marginLeft: '10px' }} onClick={() => {
            if (requireTestingPlan)
              setStep('testingPlan');
          }}>Testing Plan</div>&gt;</>
        }
        <div className={submitBreadcrumb} style={{ marginRight: '10px', marginLeft: '10px' }} onClick={() => {
          if (!submitDisabled)
            setStep('submit');
        }}>Submitter Info</div>
      </div>
    {/* Save button at top of form */}
      {!readOnly && step !== 'submit' && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '50%', paddingLeft: '20px' }}>
          <PrimaryButton style={{ minWidth: '170px', alignSelf: 'center' }} onClick={async () => { await handleSave(); }} disabled={submitting}>
            Save
          </PrimaryButton>
          <span style={{
            marginTop: '6px',
            fontSize: '0.99rem',
            color: 'rgba(0, 0, 0, 0.87)',
            lineHeight: '1.43',
            textAlign: 'left',
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif'
          }}>
            You may save your progress at any time and return later to complete your submission.
          </span>
        </div>
      )}
    </div>

    {errorMessage &&
      <div style={{ marginBottom: '20px', marginTop: '20px' }}>
        <ErrorNotice errorMessage={errorMessage} />
      </div>
    }

    {form}
  </div>);
};